import { View, Image, Button } from "@/components/ui";
import { formatPrice } from "@/lib/helpers/number";
import DefaultImg from "@/assets/images/booking/default-img-1-1.png";
import { Typography } from "../typography";
import { useRoute } from "@/components/shared/router";
import {
  isAirconService,
  type AirconServiceName,
  type BetaServiceName,
  type ServiceName,
} from "@/types/service";
import { handleOpenLink } from "@/lib/platform";
import { betaServiceItems } from "@/containers/homepage/lib";
import { airconServiceTypeOptions } from "@/constants/booking";
import { useBookingState } from "@/store/booking/useBookingState";
import { bookingRoutes } from "@/components/hooks/use-booking-route";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";

export type RecommendedService = {
  imageSrc: string;
  title: string;
  price: number;
  beta?: boolean;
  name: ServiceName | AirconServiceName | BetaServiceName;
};

type Props = RecommendedService;

export function RecommendedCard({
  imageSrc,
  title,
  price,
  name,
  beta = false,
}: Props) {
  const { push } = useRoute();

  const onClickAirconService = (name: AirconServiceName) => {
    const airconService = airconServiceTypeOptions.find(
      (item) => item.name === name,
    );
    if (!airconService) {
      throw new Error("package not found");
    }
    const packageDetailsFilter =
      useBookingState.getState().data.packageDetailsFilter;
    useBookingState.getState().setBookingState({
      packageDetailsFilter: {
        ...packageDetailsFilter,
        serviceTypes: [airconService.value],
        repeatEvery: [airconService.repeatEvery],
        repeatEveryTimes: airconService.duration,
      },
    });
    push("/service-detail/aircon-service");
  };

  const onClick = () => {
    PosthogAnalytics.track("click_recommended_card", { name });

    if (beta) {
      const link = betaServiceItems().find((item) => item.name === name)?.link;
      if (link) {
        handleOpenLink(link);
      }
    } else if (isAirconService(name) && name !== "aircon-service") {
      onClickAirconService(name as AirconServiceName);
    } else {
      push(bookingRoutes[name as ServiceName][0]);
    }
  };

  return (
    <View
      className="border-r-none relative flex flex-col overflow-hidden rounded-xl border border-secondary-border"
      onClick={onClick}
    >
      <Image
        src={imageSrc || DefaultImg}
        alt={title}
        className="aspect-square h-[146px] w-full object-cover"
        contentFit="cover"
        style={{ height: undefined, width: "100%", aspectRatio: 1 }}
      />
      <View className="flex flex-col content-between gap-1 p-2">
        <View>
          <Typography variant="body-sm" numberOfLines={1}>
            {title}
          </Typography>
          <View className="flex flex-row items-center gap-1">
            <Typography variant="caption-md" color="foreground-intermediate">
              Start from
            </Typography>
            <Typography variant="body-sm" color="brand-primary">
              {formatPrice(price, 0)}
            </Typography>
          </View>
        </View>
        <View className="flex h-12 items-center justify-center">
          <Button
            variant="secondary"
            color="CTA2"
            className="w-full"
            size="sm"
            onClick={onClick}
          >
            Book Now
          </Button>
        </View>
      </View>
    </View>
  );
}
